import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { youthAPI } from '../services/api';
import { Select, Button, Modal, Toast } from '../components/UI';
import Icon from '../components/Icon';
import YouthForm from '../components/YouthForm';

const empLabels = { 'ishlamoqda': 'Ishlamoqda', 'ishsiz': 'Ishsiz', "o'qimoqda": "O'qimoqda", 'tadbirkor': 'Tadbirkor', 'boshqa': 'Boshqa' };
const empColors = { 'ishlamoqda': '#16a34a', 'ishsiz': '#dc2626', "o'qimoqda": '#2563eb', 'tadbirkor': '#9333ea', 'boshqa': '#64748b' };
const empBgs = { 'ishlamoqda': '#f0fdf4', 'ishsiz': '#fef2f2', "o'qimoqda": '#eff6ff', 'tadbirkor': '#faf5ff', 'boshqa': '#f8fafc' };
const riskColors = { 'past': '#22c55e', "o'rta": '#f59e0b', 'yuqori': '#ef4444' };
const riskBgs = { 'past': '#f0fdf4', "o'rta": '#fffbeb', 'yuqori': '#fef2f2' };
const riskLabels = { 'past': 'Past', "o'rta": "O'rta", 'yuqori': 'Yuqori' };
const eduLabels = { "o'rta": "O'rta", "o'rta_maxsus": "O'rta maxsus", 'oliy': 'Oliy', 'magistratura': 'Magistratura', 'boshqa': 'Boshqa' };

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

/* ================================================================
   MINI COMPONENTS
   ================================================================ */

function StatCard({ icon, label, value, color, bg, sub, onClick }) {
  return (
    <div onClick={onClick} style={{
      padding: '16px 18px', borderRadius: 16, background: '#fff', border: '1px solid #e2e8f0',
      cursor: onClick ? 'pointer' : 'default', transition: 'all .2s',
      display: 'flex', alignItems: 'center', gap: 14, minWidth: 0,
    }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.06)'; }}}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        <Icon name={icon} size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color, fontWeight: 600, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}

// CSS-only Donut Chart
function DonutChart({ data, size = 160 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;
  let cumulative = 0;
  const segments = data.map(d => {
    const pct = (d.value / total) * 100;
    const start = cumulative;
    cumulative += pct;
    return { ...d, pct, start };
  });
  const gradient = segments.map(s => `${s.color} ${s.start}% ${s.start + s.pct}%`).join(', ');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <div style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: `conic-gradient(${gradient})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ width: size * 0.6, height: size * 0.6, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{total}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>Jami</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {segments.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#64748b' }}>{s.label}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginLeft: 'auto' }}>{s.value}</span>
            <span style={{ fontSize: 10, color: '#94a3b8', width: 32 }}>{Math.round(s.pct)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Horizontal Bar Chart
function HBarChart({ data, maxVal }) {
  if (!data || data.length === 0) return null;
  const mx = maxVal || Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 12, color: '#64748b' }}>{d.label}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{d.value}</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 4,
              background: d.color || 'linear-gradient(90deg, #6366f1, #8b5cf6)',
              width: `${(d.value / mx) * 100}%`,
              transition: 'width .5s ease',
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Card wrapper
function ReportCard({ title, icon, children, style: sx }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', ...sx }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name={icon} size={16} color="#6366f1" />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{title}</span>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

// Collapsible Section
function Section({ title, icon, count, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen !== false);
  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px',
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: open ? '16px 16px 0 0' : 16,
        cursor: 'pointer', fontFamily: 'inherit', transition: 'all .2s',
      }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={icon} size={16} color="#6366f1" />
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', flex: 1, textAlign: 'left' }}>{title}</span>
        {count !== undefined && (
          <span style={{ padding: '2px 10px', borderRadius: 12, background: '#f1f5f9', fontSize: 12, fontWeight: 600, color: '#64748b' }}>{count}</span>
        )}
        <Icon name="chevDown" size={16} color="#94a3b8" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      </button>
      {open && (
        <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 16px 16px', background: '#fff', overflow: 'hidden' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Active filter tags
function ActiveFilters({ filters, onRemove, onClear }) {
  const labels = {
    gender: { label: 'Jinsi', vals: { male: 'Erkak', female: 'Ayol' }},
    emp_status: { label: 'Bandlik', vals: empLabels },
    risk: { label: 'Xavf', vals: riskLabels },
    edu_level: { label: "Ta'lim" },
    tuman: { label: 'Tuman' },
    mfy: { label: 'MFY' },
    mig_status: { label: 'Migratsiya' },
    soc_low_income: { label: "Kam ta'min", vals: { '1': 'Ha' }},
    prev_registered: { label: 'Prof. hisob', vals: { '1': 'Ha' }},
    crime_convicted: { label: 'Sudlangan', vals: { '1': 'Ha' }},
  };
  const activeEntries = Object.entries(filters).filter(([k, v]) => v && k !== 'search');
  if (activeEntries.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14, alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Filterlar:</span>
      {activeEntries.map(([key, val]) => {
        const info = labels[key] || { label: key };
        const display = info.vals ? (info.vals[val] || val) : val;
        return (
          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#ede9fe', color: '#6d28d9' }}>
            {info.label}: {display}
            <button onClick={() => onRemove(key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6d28d9', padding: 0, display: 'flex' }}><Icon name="x" size={12} /></button>
          </span>
        );
      })}
      <button onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 11, textDecoration: 'underline' }}>Tozalash</button>
    </div>
  );
}


/* ================================================================
   MAIN PAGE
   ================================================================ */
export default function YouthPage() {
  const user = getUser();
  const isAdmin = user?.role === 'viloyat_admin' || user?.role === 'tuman_admin';

  const [youth, setYouth] = useState([]);
  const [stats, setStats] = useState({});
  const [filterOpts, setFilterOpts] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: '', gender: '', emp_status: '', risk: '',
    tuman: '', mfy: '', edu_level: '', mig_status: '',
    soc_low_income: '', prev_registered: '', crime_convicted: '',
  });

  const [mode, setMode] = useState('list');
  const [selected, setSelected] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState(null);

  const filterParams = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      youthAPI.list(filterParams),
      youthAPI.stats(filterParams),
    ]).then(([listRes, statsRes]) => {
      setYouth(listRes.data?.results || listRes.data || []);
      setStats(statsRes.data || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [filterParams]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    youthAPI.filterOptions({ tuman: filters.tuman || undefined })
      .then(r => setFilterOpts(r.data || {}))
      .catch(() => {});
  }, [filters.tuman]);

  const setFilter = (key, val) => {
    setFilters(prev => {
      const next = { ...prev, [key]: val };
      if (key === 'tuman') next.mfy = '';
      return next;
    });
  };
  const removeFilter = (key) => setFilter(key, '');
  const clearFilters = () => setFilters({ search: '', gender: '', emp_status: '', risk: '', tuman: '', mfy: '', edu_level: '', mig_status: '', soc_low_income: '', prev_registered: '', crime_convicted: '' });

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await youthAPI.delete(deleteId); setToast({ msg: "O'chirildi", type: 'success' }); setDeleteId(null); loadData(); }
    catch { setToast({ msg: 'Xatolik', type: 'error' }); }
  };

  const openView = async (id) => {
    try { const r = await youthAPI.get(id); setSelected(r.data); setMode('view'); }
    catch { setToast({ msg: "Ma'lumot yuklanmadi", type: 'error' }); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await youthAPI.exportExcel(filterParams);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `yoshlar_reestri_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      setToast({ msg: 'Excel yuklab olindi!', type: 'success' });
    } catch { setToast({ msg: 'Export xatolik', type: 'error' }); }
    finally { setExporting(false); }
  };

  if (mode === 'form') {
    return <YouthForm youthId={selected?.id || null} onClose={() => { setMode('list'); setSelected(null); loadData(); }} onSaved={(data) => setSelected(data)} />;
  }

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => v && k !== 'search');
  const bd = user?.role === 'viloyat_admin' ? stats.tuman_breakdown : stats.mfy_breakdown;
  const bdType = user?.role === 'viloyat_admin' ? 'tuman' : 'mfy';
  const bdLabel = user?.role === 'viloyat_admin' ? 'Tuman' : 'MFY';

  // Chart data
  const genderData = [
    { label: 'Erkak', value: stats.male || 0, color: '#3b82f6' },
    { label: 'Ayol', value: stats.female || 0, color: '#ec4899' },
  ];

  const empData = Object.entries(stats.emp_breakdown || {}).map(([k, v]) => ({
    label: empLabels[k] || k, value: v, color: empColors[k] || '#64748b',
  })).sort((a, b) => b.value - a.value);

  const eduData = Object.entries(stats.edu_breakdown || {}).map(([k, v]) => ({
    label: eduLabels[k] || k, value: v, color: '#6366f1',
  })).sort((a, b) => b.value - a.value);

  const socialData = [
    { label: "Kam ta'minlangan", value: stats.low_income || 0, color: '#f59e0b' },
    { label: 'Migrant', value: stats.migrant || 0, color: '#ca8a04' },
    { label: 'Prof. hisobda', value: stats.prev_registered || 0, color: '#ef4444' },
    { label: 'Nogironlik', value: stats.disabled || 0, color: '#6b7280' },
  ].filter(d => d.value > 0);

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* ===== HEADER ===== */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="clipboardList" size={24} color="#6366f1" /> Yoshlar reestri
          </h2>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0' }}>
            {stats.total || 0} ta yosh ro'yxatda
            {bd && bd.length > 0 && ` \u2022 ${bd.length} ta ${bdLabel.toLowerCase()}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="outline" size="sm" onClick={handleExport} icon="copy" disabled={exporting}>
            {exporting ? 'Yuklanmoqda...' : 'Excel export'}
          </Button>
          <Button onClick={() => { setSelected(null); setMode('form'); }} icon="plus">Yangi yosh</Button>
        </div>
      </div>

      {/* ===== STAT CARDS ===== */}
      <div className="ys-stat-grid" style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
        <StatCard icon="users" label="Jami yoshlar" value={stats.total || 0} color="#6366f1" bg="#ede9fe" />
        <StatCard icon="briefcase" label="Ishlamoqda" value={stats.employed || 0} color="#16a34a" bg="#dcfce7"
          sub={stats.total ? `${Math.round(stats.employed / stats.total * 100)}%` : ''} onClick={() => setFilter('emp_status', 'ishlamoqda')} />
        <StatCard icon="alertTriangle" label="Ishsiz" value={stats.unemployed || 0} color="#dc2626" bg="#fef2f2"
          sub={stats.total ? `${Math.round(stats.unemployed / stats.total * 100)}%` : ''} onClick={() => setFilter('emp_status', 'ishsiz')} />
        <StatCard icon="graduation" label="O'qimoqda" value={stats.student || 0} color="#2563eb" bg="#dbeafe"
          onClick={() => setFilter('emp_status', "o'qimoqda")} />
        <StatCard icon="plane" label="Migrant" value={stats.migrant || 0} color="#ca8a04" bg="#fefce8"
          onClick={() => setFilter('mig_status', 'ketgan')} />
        <StatCard icon="radar" label="Prof. hisobda" value={stats.prev_registered || 0} color="#dc2626" bg="#fef2f2"
          onClick={() => setFilter('prev_registered', '1')} />
      </div>

      {/* ===== CHARTS SECTION (admin only) ===== */}
      {isAdmin && stats.total > 0 && (
        <div className="ys-charts-grid" style={{ display: 'grid', gap: 16, marginBottom: 20 }}>
          <ReportCard title="Jinsi bo'yicha" icon="users">
            <DonutChart data={genderData} size={140} />
          </ReportCard>

          <ReportCard title="Bandlik holati" icon="briefcase">
            <HBarChart data={empData} />
          </ReportCard>

          <ReportCard title="Ta'lim darajasi" icon="graduation">
            <HBarChart data={eduData} />
          </ReportCard>

          {socialData.length > 0 && (
            <ReportCard title="Ijtimoiy holat" icon="heart">
              <HBarChart data={socialData} />
            </ReportCard>
          )}
        </div>
      )}

      {/* ===== HIERARCHY TABLE (admin only) ===== */}
      {isAdmin && bd && bd.length > 0 && (
        <Section title={`${bdLabel} bo'yicha hisobot`} icon="building" count={`${bd.length} ta ${bdLabel.toLowerCase()}`} defaultOpen={true}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: '#fafbff' }}>
                  {['#', bdLabel, 'Jami', 'Erkak', 'Ayol', 'Ishsiz', 'Ishlamoqda', "O'qimoqda", 'Migrant', 'Prof.hisob'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: h === '#' || h === bdLabel ? 'left' : 'center', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bd.map((row, i) => {
                  const name = row[bdType] || "Noma'lum";
                  return (
                    <tr key={i} style={{ cursor: 'pointer', transition: 'background .15s' }}
                      onClick={() => setFilter(bdType, name)}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{i + 1}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon name="building" size={14} color="#6366f1" />
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: 15, fontWeight: 800, color: '#6366f1' }}>{row.jami}</td>
                      <td style={{ textAlign: 'center' }}><span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: '#dbeafe', color: '#2563eb' }}>{row.erkak}</span></td>
                      <td style={{ textAlign: 'center' }}><span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: '#fce7f3', color: '#db2777' }}>{row.ayol}</span></td>
                      <td style={{ textAlign: 'center' }}>{row.ishsiz > 0 ? <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: '#fef2f2', color: '#dc2626' }}>{row.ishsiz}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#16a34a' }}>{row.ishlamoqda}</td>
                      <td style={{ textAlign: 'center', fontSize: 13, color: '#2563eb' }}>{row.talaba}</td>
                      <td style={{ textAlign: 'center' }}>{row.migrant > 0 ? <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: '#fefce8', color: '#ca8a04' }}>{row.migrant}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.profilaktik > 0 ? <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: '#fef2f2', color: '#dc2626' }}>{row.profilaktik}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                  <td style={{ padding: '12px 14px' }} />
                  <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>JAMI</td>
                  <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: 15, fontWeight: 800, color: '#6366f1' }}>{bd.reduce((s, r) => s + r.jami, 0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#2563eb' }}>{bd.reduce((s, r) => s + r.erkak, 0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#db2777' }}>{bd.reduce((s, r) => s + r.ayol, 0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>{bd.reduce((s, r) => s + r.ishsiz, 0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a' }}>{bd.reduce((s, r) => s + r.ishlamoqda, 0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#2563eb' }}>{bd.reduce((s, r) => s + r.talaba, 0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#ca8a04' }}>{bd.reduce((s, r) => s + r.migrant, 0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: '#dc2626' }}>{bd.reduce((s, r) => s + r.profilaktik, 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Section>
      )}

      {/* ===== YOUTH LIST ===== */}
      <Section title="Yoshlar ro'yxati" icon="users" count={loading ? '...' : youth.length} defaultOpen={!isAdmin}>
        {/* Search + Filters */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: hasActiveFilters ? 12 : 0, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
              <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', zIndex: 1 }}><Icon name="search" size={16} /></div>
              <input type="text" value={filters.search} onChange={e => setFilter('search', e.target.value)}
                placeholder="Qidirish (FIO, telefon, JSHSHIR)..."
                style={{ width: '100%', padding: '10px 16px 10px 40px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fafbff', fontSize: 13, fontFamily: 'inherit', outline: 'none', transition: 'border .2s', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = '#6366f1'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} style={{
              padding: '10px 16px', borderRadius: 10, border: `1.5px solid ${showFilters || hasActiveFilters ? '#6366f1' : '#e2e8f0'}`,
              background: showFilters || hasActiveFilters ? '#ede9fe' : '#fafbff',
              color: showFilters || hasActiveFilters ? '#6366f1' : '#64748b',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 12, fontFamily: 'inherit',
            }}>
              <Icon name="target" size={14} /> Filterlar
              {hasActiveFilters && <span style={{ width: 18, height: 18, borderRadius: 9, background: '#6366f1', color: '#fff', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{Object.entries(filters).filter(([k, v]) => v && k !== 'search').length}</span>}
            </button>
          </div>

          <ActiveFilters filters={filters} onRemove={removeFilter} onClear={clearFilters} />

          {showFilters && (
            <div style={{ marginTop: 12, display: 'grid', gap: 10 }} className="ys-filter-grid">
              {user?.role === 'viloyat_admin' && filterOpts.tumans?.length > 0 && (
                <Select label="Tuman" value={filters.tuman} onChange={v => setFilter('tuman', v)}
                  options={[{ value: '', label: 'Barcha tumanlar' }, ...filterOpts.tumans.map(t => ({ value: t, label: t }))]} />
              )}
              {(user?.role === 'viloyat_admin' || user?.role === 'tuman_admin') && filterOpts.mfys?.length > 0 && (
                <Select label="MFY" value={filters.mfy} onChange={v => setFilter('mfy', v)}
                  options={[{ value: '', label: 'Barcha MFYlar' }, ...filterOpts.mfys.map(m => ({ value: m, label: m }))]} />
              )}
              <Select label="Jinsi" value={filters.gender} onChange={v => setFilter('gender', v)}
                options={[{ value: '', label: 'Barchasi' }, { value: 'male', label: 'Erkak' }, { value: 'female', label: 'Ayol' }]} />
              <Select label="Bandlik" value={filters.emp_status} onChange={v => setFilter('emp_status', v)}
                options={[{ value: '', label: 'Barchasi' }, ...Object.entries(empLabels).map(([v, l]) => ({ value: v, label: l }))]} />
              {filterOpts.edu_levels?.length > 0 && (
                <Select label="Ta'lim" value={filters.edu_level} onChange={v => setFilter('edu_level', v)}
                  options={[{ value: '', label: 'Barchasi' }, ...filterOpts.edu_levels.map(e => ({ value: e, label: eduLabels[e] || e }))]} />
              )}
              <Select label="Xavf darajasi" value={filters.risk} onChange={v => setFilter('risk', v)}
                options={[{ value: '', label: 'Barchasi' }, ...Object.entries(riskLabels).map(([v, l]) => ({ value: v, label: l }))]} />
              <Select label="Migratsiya" value={filters.mig_status} onChange={v => setFilter('mig_status', v)}
                options={[{ value: '', label: 'Barchasi' }, { value: 'ketgan', label: 'Ketgan' }, { value: 'qaytgan', label: 'Qaytgan' }]} />
              <Select label="Prof. hisobda" value={filters.prev_registered} onChange={v => setFilter('prev_registered', v)}
                options={[{ value: '', label: 'Barchasi' }, { value: '1', label: 'Ha' }]} />
            </div>
          )}
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 750 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#fafbff' }}>
                {['#', 'F.I.O', 'Yosh', 'Jinsi', 'Telefon',
                  ...(user?.role !== 'mfy' ? ['MFY'] : []),
                  ...(user?.role === 'viloyat_admin' ? ['Tuman'] : []),
                  'Bandlik', 'Xavf', 'Amallar'
                ].map(h => (
                  <th key={h} style={{ padding: '11px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} style={{ padding: 50, textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ display: 'inline-block', width: 28, height: 28, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
                  <div style={{ marginTop: 10, fontSize: 13 }}>Yuklanmoqda...</div>
                </td></tr>
              ) : youth.length === 0 ? (
                <tr><td colSpan={12} style={{ padding: 50, textAlign: 'center', color: '#94a3b8' }}>
                  <Icon name="search" size={28} />
                  <div style={{ marginTop: 8, fontSize: 14, fontWeight: 500 }}>Hech qanday yosh topilmadi</div>
                </td></tr>
              ) : youth.map((y, i) => (
                <tr key={y.id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background .15s' }}
                  onClick={() => openView(y.id)}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>{y.full_name}</div>
                    {y.permanent_address && <div style={{ fontSize: 10, color: '#cbd5e1', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{y.permanent_address}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: '#64748b' }}>{y.age}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ width: 24, height: 24, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: y.gender === 'male' ? '#dbeafe' : '#fce7f3', color: y.gender === 'male' ? '#2563eb' : '#db2777' }}>
                      {y.gender === 'male' ? 'E' : 'A'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{y.phone}</td>
                  {user?.role !== 'mfy' && <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{y.mfy}</td>}
                  {user?.role === 'viloyat_admin' && <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>{y.tuman}</td>}
                  <td style={{ padding: '10px 12px' }}>
                    {y.emp_status && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: empBgs[y.emp_status] || '#f8fafc', color: empColors[y.emp_status] || '#64748b', whiteSpace: 'nowrap' }}>{empLabels[y.emp_status] || y.emp_status}</span>}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {y.prev_risk_level && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: riskBgs[y.prev_risk_level] || '#f8fafc', color: riskColors[y.prev_risk_level] || '#94a3b8' }}>{riskLabels[y.prev_risk_level] || y.prev_risk_level}</span>}
                  </td>
                  <td style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => { setSelected(y); setMode('form'); }} title="Tahrirlash"
                        style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#ede9fe'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      ><Icon name="edit" size={13} /></button>
                      <button onClick={() => setDeleteId(y.id)} title="O'chirish"
                        style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      ><Icon name="trash" size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* View Modal */}
      <Modal open={mode === 'view'} onClose={() => { setMode('list'); setSelected(null); }} title={selected?.full_name || "Ma'lumotlar"} width={640}>
        {selected && <YouthViewDetail data={selected} onEdit={() => setMode('form')} />}
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="O'chirishni tasdiqlang">
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>Ushbu yoshni reestrdan o'chirishni xohlaysizmi?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteId(null)}>Bekor qilish</Button>
          <Button variant="danger" onClick={handleDelete}>O'chirish</Button>
        </div>
      </Modal>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .ys-stat-grid { grid-template-columns: repeat(6, 1fr); }
        .ys-charts-grid { grid-template-columns: repeat(2, 1fr); }
        .ys-filter-grid { grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 1200px) {
          .ys-stat-grid { grid-template-columns: repeat(3, 1fr); }
          .ys-charts-grid { grid-template-columns: 1fr 1fr; }
          .ys-filter-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 768px) {
          .ys-stat-grid { grid-template-columns: repeat(2, 1fr); }
          .ys-charts-grid { grid-template-columns: 1fr; }
          .ys-filter-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 480px) {
          .ys-stat-grid { grid-template-columns: 1fr; }
          .ys-filter-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}


/* ================================================================
   VIEW DETAIL (Modal)
   ================================================================ */
function YouthViewDetail({ data, onEdit }) {
  const sections = [
    { title: "Asosiy ma'lumotlar", icon: 'fingerprint', color: '#6366f1', fields: [
      ['Familiya', data.last_name], ['Ism', data.first_name], ['Otasining ismi', data.middle_name],
      ['Jinsi', data.gender === 'male' ? 'Erkak' : 'Ayol'], ['Yoshi', data.age], ["Tug'ilgan sana", data.birth_date],
      ["Tug'ilgan joy", data.birth_place], ['Fuqaroligi', data.citizenship], ['Millati', data.nationality],
      ['Doimiy manzil', data.permanent_address], ['Vaqtinchalik manzil', data.temporary_address],
      ['Telefon', data.phone], ['Email', data.email], ['JSHSHIR', data.jshshir],
      ['Pasport', `${data.passport_series || ''} ${data.passport_number || ''}`.trim()],
      ['Viloyat', data.viloyat], ['Tuman', data.tuman], ['MFY', data.mfy],
    ]},
    { title: "Ta'lim holati", icon: 'graduation', color: '#2563eb', fields: [
      ["Ta'lim darajasi", data.edu_level], ['Muassasa', data.edu_institution], ["Turi", data.edu_type],
      ['Kurs', data.edu_course], ["Yo'nalish", data.edu_specialty], ['Kontrakt/Grant', data.edu_funding],
      ["O'qish shakli", data.edu_form], ['Holati', data.edu_graduated],
    ]},
    { title: 'Ish bilan bandligi', icon: 'briefcase', color: '#16a34a', fields: [
      ['Bandlik', empLabels[data.emp_status] || data.emp_status], ['Ish joyi', data.emp_workplace],
      ['Lavozimi', data.emp_position], ['Ish turi', data.emp_type], ['Maosh', data.emp_salary_range],
    ]},
    { title: 'Ijtimoiy toifasi', icon: 'heart', color: '#ec4899', fields: [
      ["Kam ta'minlangan", data.soc_low_income ? 'Ha' : ''], ['Nogironlik', data.soc_disability],
      ['Yetim', data.soc_orphan], ['Uy-joy', data.soc_housing], ['Nafaqa', data.soc_benefit_type],
    ]},
    { title: 'Migratsiya', icon: 'plane', color: '#ca8a04', fields: [
      ['Maqomi', data.mig_status], ['Davlat', data.mig_country],
      ['Chiqish', data.mig_departure_date], ['Qaytish', data.mig_return_date],
    ]},
    { title: 'Tadbirkorlik', icon: 'store', color: '#9333ea', fields: [
      ['Maqomi', data.biz_status], ['STIR', data.biz_stir], ['Faoliyat', data.biz_activity_type],
    ]},
    { title: 'Profilaktik nazorat', icon: 'radar', color: '#dc2626', fields: [
      ['Hisobda', data.prev_registered ? 'Ha' : ''], ['Xavf', data.prev_risk_level],
      ['Nazorat turi', data.prev_type], ['Inspektor', data.prev_inspector],
    ]},
    { title: 'Jazoni ijro etish', icon: 'gavel', color: '#78350f', fields: [
      ['Sudlangan', data.crime_convicted ? 'Ha' : ''], ['Modda', data.crime_article],
      ['Jazo turi', data.crime_punishment_type],
    ]},
  ];

  return (
    <div>
      {sections.map(sec => {
        const filled = sec.fields.filter(([, v]) => v && String(v).trim());
        if (filled.length === 0) return null;
        return (
          <div key={sec.title} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: sec.color, textTransform: 'uppercase', marginBottom: 10, letterSpacing: '.5px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name={sec.icon} size={14} color={sec.color} /> {sec.title}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px' }}>
              {filled.map(([label, val]) => (
                <div key={label} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                  <span style={{ color: '#94a3b8', minWidth: 110, flexShrink: 0 }}>{label}:</span>
                  <span style={{ color: '#1e293b', fontWeight: 500 }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
        <Button variant="primary" onClick={onEdit} size="sm" icon="edit">Tahrirlash</Button>
      </div>
    </div>
  );
}
